import { ServerBillInfo } from '@/api/type-server'
import { paymentTerms, PaymentType } from '@/consts/machine'
import { formatDuration, formatPrice } from '@/utils/formatter'
import { formatOldMachineName } from '@/utils/node'

export enum BonusType {
  Bean = 1,
  Time = 2,
}

export enum DiscountType {
  Coupon = 0,
  Time = 1,
  Bean = 2,
}

export class MachineBill {
  billType = '-'
  bill: ServerBillInfo
  usedTimeStr = ''
  price = 0
  priceStr = ''

  constructor(sourceData: ServerBillInfo) {
    this.bill = sourceData
    this.initNodeBillInfo()
  }

  get displayID() {
    return this.bill.displayID
  }

  get billTime() {
    return this.bill.billTime.seconds * 1000
  }

  get startTime() {
    return this.bill.timeInfo.startTime.seconds * 1000
  }

  get endTime() {
    return (this.bill.timeInfo.endTime?.seconds || 0) * 1000 || Date.now()
  }

  get isRunning() {
    return this.bill.type === 1
  }

  get amount() {
    return this.bill.amount
  }

  get type() {
    return this.bill.type
  }

  get subtype() {
    return this.bill.subType
  }

  get billingInfo() {
    return this.bill.Info.BillingInfo
  }

  get hwItemBillInfo() {
    return this.bill.Info.HwItemBillInfo
  }

  get timeBonusBillInfo() {
    return this.bill.Info.TimeBonusBillInfo
  }

  get hostName() {
    if (this.timeBonusBillInfo) {
      return Object.keys(this.timeBonusBillInfo.bonusConds.conds[0].gpuModelLimit.models).join(',')
    }

    return formatOldMachineName((this.billingInfo as any) || this.hwItemBillInfo)
  }

  get typeStr() {
    return BILL_SUBTYPE_DISPLAY[this.subtype] || BILL_TYPE_DISPLAY[this.type] || '-'
  }

  initNodeBillInfo() {
    if (this.billingInfo) {
      this.billType = 'Hourly'
      const { usedTimeSec, hardware } = this.billingInfo
      this.formatBillInfo({
        usedTimeSec,
        price: Math.round(hardware.discountPriceMillicent / 1000),
      })
    } else if (this.hwItemBillInfo) {
      const { availableSecs, paymentType } = this.hwItemBillInfo
      this.billType = paymentTerms[paymentType as PaymentType]?.label || ''
      this.formatBillInfo({ usedTimeSec: availableSecs })
    }
  }

  formatBillInfo({ usedTimeSec, price }: { usedTimeSec?: number; price?: number } = {}) {
    this.usedTimeStr = usedTimeSec !== undefined ? formatDuration(usedTimeSec) : '-'
    this.price = price || 0
    this.priceStr = price ? `${formatPrice(this.price, false)}/hour` : '-'
  }
}

const BILL_SUBTYPE = {
  Invalid: 0, // no sub type
  BillingTransfer: 1, // transfer money from parent account to children account, e.g. oversea account
  PayGroupBonus: 2, // pay bonus for group nember, including user himself
  EduHwBonusPurchase: 3, // edu hw bonus purchase
  OrderToBilling: 4, // mark bill of OrderToBilling
  EduStorageDiscount: 5, // mark for edu storage discount
  TimeLimitedStorageRenew: 6,
  TimeLimitedStorageAutoRenew: 7,
  RechargeByCMB: 8,
  RechargeByAlipay: 9,
  RechargeByCorporateAccounts: 10, // used in MMS/GRPC
  RechargeByTaoBao: 11, // used in MMS/GRPC
  GiftByCooperation: 12, // gift money by cooperation, used in MMS/GRPC
  GiftByActivity: 13, // gift money by activity, used in MMS/GRPC
  GiftForExperience: 14, // experience reward, used in MMS/GRPC
  GiftByInternal: 15, // internal gift, used in MMS/GRPC
  ManualDeduction: 16, // manual deduction, used in MMS/GRPC
  TimeLimitedStorageFirstBuy: 17,
  TimeLimitedStorageModify: 18,
  GroupStorageCreate: 19,
  GroupStorageModify: 20,
  GroupStorageRenew: 21,
  GroupStorageAutoRenew: 22,
  PayCourseBonus: 23, // same info struct with PayGroupBonus, prepare for filter usage
  TimeLimitedMachine: 24,
  TimeLimitedMachineRenew: 25,
}

export const BILL_SUBTYPE_DISPLAY = {
  [BILL_SUBTYPE.BillingTransfer]: 'International Site Credits',
  [BILL_SUBTYPE.PayGroupBonus]: 'Team Computing Power Beans',
  [BILL_SUBTYPE.EduHwBonusPurchase]: 'Student Package',
  [BILL_SUBTYPE.OrderToBilling]: 'Package to Pay-As-You-Go Conversion',
  [BILL_SUBTYPE.TimeLimitedStorageFirstBuy]: 'Data Hub Package',
  [BILL_SUBTYPE.TimeLimitedStorageModify]: 'Data Hub Package Modification',
  [BILL_SUBTYPE.TimeLimitedStorageRenew]: 'Data Hub Package Renewal',
  [BILL_SUBTYPE.TimeLimitedStorageAutoRenew]: 'Data Hub Auto-Renewal',
  [BILL_SUBTYPE.RechargeByCMB]: 'Main Site Recharge',
  [BILL_SUBTYPE.RechargeByAlipay]: 'Main Site Recharge via Alipay',
  [BILL_SUBTYPE.RechargeByCorporateAccounts]: 'Corporate Account Recharge',
  [BILL_SUBTYPE.RechargeByTaoBao]: 'Taobao Recharge',
  [BILL_SUBTYPE.GiftByCooperation]: 'Gift by Partnership',
  [BILL_SUBTYPE.GiftByActivity]: 'Gift by Activity',
  [BILL_SUBTYPE.GiftForExperience]: 'Trial Credits',
  [BILL_SUBTYPE.GiftByInternal]: 'Internal Staff Recharge',
  [BILL_SUBTYPE.ManualDeduction]: 'Manual Deduction',
  [BILL_SUBTYPE.GroupStorageCreate]: 'Team Storage Plan',
  [BILL_SUBTYPE.GroupStorageModify]: 'Team Storage Plan Modification',
  [BILL_SUBTYPE.GroupStorageRenew]: 'Team Storage Plan Renewal',
  [BILL_SUBTYPE.GroupStorageAutoRenew]: 'Team Storage Auto-Renewal',
  [BILL_SUBTYPE.PayCourseBonus]: 'Course Computing Power Beans/Time Card',
  [BILL_SUBTYPE.TimeLimitedMachine]: 'Time-Limited Machine Rental',
  [BILL_SUBTYPE.TimeLimitedMachineRenew]: 'Time-Limited Machine Rental Renewal',
}

export const BILL_TYPE_DISPLAY: any = {
  1: 'Time-Based Rental',
  2: 'Time-Based Rental',
  3: 'Recharge',
  6: 'Gift',
  7: 'Consumption',
  8: 'Withdrawal',
  10: 'Consumption',
  11: 'Refund',
}

export const BILL_CURRENCY_DISPLAY = {
  1: 'USD',
  2: 'TWD',
}
